"use client";

import { useState, useTransition } from "react";
import { toast } from "sonner";
import { Check, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RESTORATION_CATEGORY_OPTIONS } from "@/lib/restorationBlocks";
import type { RestorationProblemMeta } from "@/lib/server-actions/restoration/_shared";
import { upsertProblemMeta } from "@/lib/server-actions/restoration/examSessions";

interface ProblemMetaAdminPanelProps {
  meta: RestorationProblemMeta;
  onSaved: () => void;
  onCancel: () => void;
}

/**
 * JCG-1572: 관리자용 문제 메타 편집 패널.
 * - 카테고리: `RESTORATION_CATEGORY_OPTIONS` 화이트리스트 + "없음" 옵션
 * - 요약: 자유 텍스트 (null 가능)
 *
 * `ProblemAccordion` 내부에서 관리자가 "메타 편집" 클릭 시 인라인 렌더.
 */
export default function ProblemMetaAdminPanel({
  meta,
  onSaved,
  onCancel,
}: ProblemMetaAdminPanelProps) {
  const [category, setCategory] = useState<string>(meta.category ?? "");
  const [summary, setSummary] = useState<string>(meta.summary ?? "");
  const [isPending, startTransition] = useTransition();

  const handleSave = () => {
    startTransition(async () => {
      const result = await upsertProblemMeta(
        meta.session_id,
        meta.problem_number,
        category.trim() || null,
        summary.trim() || null
      );
      if (result.success) {
        toast.success(`${meta.problem_number}번 메타가 저장되었습니다.`);
        onSaved();
      } else {
        toast.error(result.error || "메타 저장에 실패했습니다.");
      }
    });
  };

  return (
    <div className="rounded-md border border-blue-200 dark:border-blue-900/60 bg-blue-50/40 dark:bg-blue-950/20 p-3 flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-48">
          <label className="block text-xs font-medium text-gray-700 dark:text-neutral-300 mb-1">
            카테고리
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full h-8 rounded-md border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-2 text-sm"
          >
            <option value="">(없음)</option>
            {RESTORATION_CATEGORY_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-medium text-gray-700 dark:text-neutral-300 mb-1">
            요약
          </label>
          <Textarea
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="예: 결합도의 종류와 의미"
            className="min-h-[60px] text-sm"
          />
        </div>
      </div>
      <div className="flex items-center gap-2 justify-end">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onCancel}
          disabled={isPending}
        >
          <X className="w-3 h-3 mr-1" />
          취소
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={isPending}
        >
          {isPending ? (
            <>
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              저장 중...
            </>
          ) : (
            <>
              <Check className="w-3 h-3 mr-1" />
              저장
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
